import * as vp from 'vopay';
import { TextFieldPresets, Icons } from 'vopay';

import type { FieldData } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';
import { useLockedFeatures } from '../../providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '../../providers/LockedFeatureDialogue.provider';
import { JSX } from 'react';

const { Box, ErrorText, Icon, Select, TextField, Toggle, GoogleAddressField, Checkbox } = vp;

export function createCurrencyField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return createTextField(fieldData, ctx, 'currency', 'dollar');
}

export function createEmailField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return createTextField(fieldData, ctx, 'email');
}

export function createNumberField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return createTextField(fieldData, ctx, 'numericWholePositive');
}
export function createNumberFieldLead<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return createTextField(fieldData, ctx, 'number');
}

export function createErrorMessage<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  const errorMessage = ctx.errors[fieldData.fieldName] as string;
  return errorMessage ? (
    <Box key={`${fieldData.fieldName}-error-message`}>
      <Icon icon="asterisk" size="xsm" color="error" />
      <ErrorText error={errorMessage} />
    </Box>
  ) : (
    <div key={`no-${fieldData.fieldName}-error-message`} />
  );
}

export function createPercentageField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return createTextField(fieldData, ctx, 'numeric', 'percentage');
}
export type SelectOptions = {
  noOrdering?: boolean;
  placeholderColor?: string;
  showValue?: boolean;
  orderByValues?: boolean;
  renderDropdownHeader?: () => React.ReactNode;
};
export function createSelectField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  options?: SelectOptions,
): JSX.Element {
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();

  return (
    <Select
      variant="plain"
      placeholder={fieldData.placeholder || ''}
      options={fieldData.getSelectOptions ? fieldData.getSelectOptions(ctx) : {}}
      lockedValues={lockedFeatures.LockedSelectOptions}
      lockedCallback={(e: string) => updateIsLocked(true, e)}
      name={fieldData.fieldName}
      onBlur={ctx.handleBlur}
      onChange={ctx.handleFieldChange}
      noOrdering={options?.noOrdering}
      placeholderColor={options?.placeholderColor}
      value={String(ctx.values[fieldData.fieldName])}
      orderByValues={options?.orderByValues}
      showValue={options?.showValue}
      renderDropdownHeader={options?.renderDropdownHeader}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      disabled={fieldData.getInputState(ctx) === 'disabled'}
    />
  );
}

export function createToggleField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return (
    <Box jc="fe" pt="sm" pb="sm">
      <Toggle
        name={fieldData.fieldName}
        onBlur={ctx.handleBlur}
        onChange={ctx.handleToggleChange}
        value={Boolean(ctx.values[fieldData.fieldName])}
        disabled={fieldData.getInputState(ctx) === 'disabled'}
        error={
          ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
            ? ctx.errors[fieldData.fieldName]?.toString()
            : null
        }
      />
    </Box>
  );
}

export function createCheckboxField<T>(fieldData: FieldData<T>, ctx: MultiStepFormCtx<T>): JSX.Element {
  return (
    <Box jc="fe" pt="sm" pb="sm" pe="xmd">
      <Checkbox
        name={fieldData.fieldName}
        onBlur={ctx.handleBlur}
        onChange={ctx.handleFieldChange}
        value={Boolean(ctx.values[fieldData.fieldName])}
        disabled={fieldData.getInputState(ctx) === 'disabled'}
        error={
          ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
            ? ctx.errors[fieldData.fieldName]?.toString()
            : null
        }
      />
    </Box>
  );
}

export function createTextField<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  preset: keyof TextFieldPresets = 'text',
  icon?: Icons,
): JSX.Element {
  return (
    <TextField
      variant="plain"
      preset={preset}
      placeholder={fieldData.placeholder}
      name={fieldData.fieldName}
      onBlur={ctx.handleBlur}
      onChange={ctx.handleFieldChange}
      value={String(ctx.values[fieldData.fieldName] || '')}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      elementLeft={icon && <Icon icon={icon} size="sm" />}
      disabled={fieldData.getInputState(ctx) === 'disabled'}
    />
  );
}

export function createTextGoogleAddressFeild<T>(
  fieldData: FieldData<T>,
  ctx: MultiStepFormCtx<T>,
  preset: keyof TextFieldPresets = 'text',
  icon?: Icons,
): JSX.Element {
  const validCountries = Object.keys(fieldData?.getGoogleCountries ? fieldData?.getGoogleCountries(ctx) : {});
  return (
    <GoogleAddressField
      variant="plain"
      ISOCountry={validCountries}
      preset={preset}
      placeholder={fieldData.placeholder}
      name={fieldData.fieldName}
      onBlur={ctx.handleBlur}
      onChange={ctx.handleFieldChange}
      value={String(ctx.values[fieldData.fieldName] || '')}
      error={
        ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
          ? ctx.errors[fieldData.fieldName]?.toString()
          : null
      }
      elementLeft={icon && <Icon icon={icon} size="sm" />}
      disabled={fieldData.getInputState(ctx) === 'disabled'}
      onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
        if (fieldData.handleGoogleResults) {
          fieldData.handleGoogleResults(ctx, addressComponents);
        }
      }}
    />
  );
}

export const validationMessages = {
  invalidEmail: 'This email address is not valid.',
  reserveToActivate: 'Please enter a fee reserve amount.',
  minOneDollar: 'Please enter a value of at least $1.00.',
  minOne: 'Please enter a value of at least 1.',
  missingEmail: 'Please enter an email address.',
  noSpecialChars: 'Special characters are not enabled for this field.',
  minAmount: 'Please enter a minimum amount.',
  maxAmount: 'Please enter a maximum amount.',
  fee: 'Please enter a fee amount.',
  percentage: 'Please enter a percentage.',
  numbers: 'Non numeric characters are not enabled for this field.',
};

export const matchers = {
  alpha: 'a-z',
  numeric: '0-9',
  frenchChars: 'àâçéèêëîïôûùüÿñæœ',
  whitespace: ' ',
  specialChars: "&\\-()@'",
};

export function buildMatcher(_matchers: string[]): string {
  return `^[${_matchers.reduce((accumulator, current) => `${accumulator}${current}`, '')}]*$`;
}
